import apiClient from './api';

export interface SocialPage {
  id: string;
  organization_id: string;
  platform: 'facebook' | 'instagram';
  platform_page_id: string;
  page_name: string;
  page_username?: string;
  page_avatar_url?: string;
  is_active: boolean;
  daily_dm_limit: number;
  dedup_enabled: boolean;
  cooldown_seconds: number;
  trigger_post_comments: boolean;
  trigger_story_replies: boolean;
  trigger_live_comments: boolean;
  trigger_ad_comments: boolean;
  catchall_mode: 'ignore' | 'ai_filter';
  catchall_dm_text?: string;
  catchall_comment_reply_enabled: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Enriched by list endpoint
  keyword_count?: number;
  stats?: SocialPageStats;
}

export interface SocialKeywordRule {
  id: string;
  social_page_id: string;
  keyword: string;
  is_active: boolean;
  dm_text: string;
  comment_reply_enabled: boolean;
  comment_reply_variations: string[];
  total_triggered: number;
  total_dms_sent: number;
  total_conversions: number;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface SocialInteraction {
  id: string;
  social_page_id: string;
  platform: string;
  post_id: string;
  post_text?: string;
  comment_id: string;
  comment_text: string;
  commenter_platform_id: string;
  commenter_name?: string;
  commenter_username?: string;
  matched: boolean;
  match_type?: string;
  keyword_matched?: string;
  comment_replied: boolean;
  comment_reply_text?: string;
  dm_sent: boolean;
  dm_text?: string;
  dm_error?: string;
  conversation_id?: string;
  converted: boolean;
  conversion_type?: string;
  conversion_value?: number;
  created_at: string;
}

export interface SocialPageStats {
  total_comments: number;
  total_dms: number;
  total_replies: number;
  total_conversions: number;
  total_revenue: number;
}

class SocialCaptureServiceClass {
  async getPages(): Promise<SocialPage[]> {
    const res = await apiClient.get<any>('/social-capture/pages');
    return res.data?.data || [];
  }

  async connectPage(data: {
    platform: string;
    platform_page_id: string;
    page_name: string;
    page_username?: string;
    page_avatar_url?: string;
    credentials: Record<string, unknown>;
  }): Promise<SocialPage> {
    const res = await apiClient.post<any>('/social-capture/pages', data);
    return res.data?.data;
  }

  async getPage(id: string): Promise<SocialPage> {
    const res = await apiClient.get<any>(`/social-capture/pages/${id}`);
    return res.data?.data;
  }

  async updatePage(id: string, data: Partial<SocialPage>): Promise<SocialPage> {
    const res = await apiClient.patch<any>(`/social-capture/pages/${id}`, data);
    return res.data?.data;
  }

  async deletePage(id: string): Promise<void> {
    await apiClient.delete(`/social-capture/pages/${id}`);
  }

  async getKeywords(pageId: string): Promise<SocialKeywordRule[]> {
    const res = await apiClient.get<any>(`/social-capture/pages/${pageId}/keywords`);
    return res.data?.data || [];
  }

  async createKeyword(pageId: string, data: {
    keyword: string;
    dm_text: string;
    comment_reply_enabled?: boolean;
    comment_reply_variations?: string[];
  }): Promise<SocialKeywordRule> {
    const res = await apiClient.post<any>(`/social-capture/pages/${pageId}/keywords`, data);
    return res.data?.data;
  }

  async updateKeyword(pageId: string, keywordId: string, data: Partial<SocialKeywordRule>): Promise<SocialKeywordRule> {
    const res = await apiClient.patch<any>(`/social-capture/pages/${pageId}/keywords/${keywordId}`, data);
    return res.data?.data;
  }

  async deleteKeyword(pageId: string, keywordId: string): Promise<void> {
    await apiClient.delete(`/social-capture/pages/${pageId}/keywords/${keywordId}`);
  }

  async getStats(pageId: string, since?: string): Promise<SocialPageStats> {
    const params = since ? `?since=${since}` : '';
    const res = await apiClient.get<any>(`/social-capture/pages/${pageId}/stats${params}`);
    return res.data?.data || { total_comments: 0, total_dms: 0, total_replies: 0, total_conversions: 0, total_revenue: 0 };
  }

  async getActivity(pageId: string, params?: {
    filter?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ data: SocialInteraction[]; meta: { totalItems: number; totalPages: number; perPage: number; currentPage: number } }> {
    const searchParams = new URLSearchParams();
    if (params?.filter) searchParams.set('filter', params.filter);
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const qs = searchParams.toString();
    const res = await apiClient.get<any>(`/social-capture/pages/${pageId}/activity${qs ? `?${qs}` : ''}`);
    return {
      data: res.data?.data || [],
      meta: res.data?.meta || { totalItems: 0, totalPages: 0, perPage: 25, currentPage: 1 },
    };
  }
}

export const socialCaptureService = new SocialCaptureServiceClass();
